import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import { useNavigate, useParams, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { audiencesService } from "../../services/audiences";
import { useDeleteAudienceMutation } from "../../redux/api/audiencesApi";
import { trackEvent } from "../../utils/tracking";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { getPlatformColor, getPlatformIcon, PLATFORMS } from "../../utils/platforms";
import AudienceUploadTimelineHistogram from "../../components/audience/AudienceUploadTimelineHistogram";
import AudienceRosterModal from "../../components/audience/AudienceRosterModal";
import { getAudienceDisplayName, getPlatformLabel } from "../../constants/audiences";

const SETTINGS_NAV = [
  {
    id: "configuration",
    label: "Configuration",
    scrollId: "audience-settings-configuration",
    subtitle: "Audience name and type-specific setup.",
  },
  {
    id: "sync",
    label: "Sync Status",
    scrollId: "audience-settings-sync",
    subtitle: "Refresh creator data and fit scores for this audience.",
  },
  {
    id: "manage",
    label: "Manage",
    scrollId: "audience-settings-manage",
    subtitle: "Irreversible actions for this audience.",
  },
];

function legacySettingsScrollId(pathname) {
  if (pathname.includes("/settings/management")) return "audience-settings-manage";
  if (pathname.includes("/settings/sync")) return "audience-settings-sync";
  if (pathname.includes("/settings/configuration")) return "audience-settings-configuration";
  return null;
}

/** Old URLs like /audience/:hash/settings/sync/ redirect to single-page settings with a hash. */
function LegacyAudienceSettingsRedirect() {
  const { hash } = useParams();
  const location = useLocation();
  const scrollId = legacySettingsScrollId(location.pathname);
  if (!scrollId) {
    return <Navigate to={`/audience/${hash}/settings/`} replace />;
  }
  return <Navigate to={`/audience/${hash}/settings/#${scrollId}`} replace />;
}

const SettingsPage = ({ audience, onAudienceUpdated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hash } = useParams();
  const [deleteAudience, { isLoading: isDeletingAudience }] = useDeleteAudienceMutation();
  const [isSyncing, setIsSyncing] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("configuration");

  useEffect(() => {
    setNameDraft(audience?.name ?? "");
  }, [audience?.hash, audience?.name]);

  const scrollToSection = useCallback((scrollId) => {
    const el = document.getElementById(scrollId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useLayoutEffect(() => {
    const h = (location.hash || "").replace(/^#/, "");
    if (h && document.getElementById(h)) {
      requestAnimationFrame(() => {
        document.getElementById(h)?.scrollIntoView({ behavior: "auto", block: "start" });
      });
    }
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const main = document.querySelector("main.overflow-y-auto");
    if (!main) return;

    const sections = SETTINGS_NAV.map((s) => document.getElementById(s.scrollId)).filter(Boolean);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
        if (visible[0]?.target?.id) {
          const match = SETTINGS_NAV.find((s) => s.scrollId === visible[0].target.id);
          if (match) setActiveSection(match.id);
        }
      },
      { root: main, rootMargin: "-72px 0px -45% 0px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [hash]);

  const handleSync = () => {
    setIsSyncing(true);
    audiencesService.syncAudience(
      hash,
      () => {
        setIsSyncing(false);
        trackEvent("sync_audience", { audience_hash: hash });
        navigate(`/audience/${hash}/populating/`);
      },
      (error) => {
        console.error("Failed to sync audience:", error);
        alert(getApiErrorMessage(error, "Failed to sync audience. Please try again."));
        setIsSyncing(false);
      }
    );
  };

  const handleDelete = async () => {
    const audienceName = getAudienceDisplayName(audience);
    if (
      !window.confirm(
        `Are you sure you want to delete "${audienceName}"? This action cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteAudience({
        hash,
        projectHash: audience.project_hash,
      }).unwrap();
      navigate("/research/");
    } catch (error) {
      console.error("Failed to delete audience:", error);
      alert("Failed to delete audience. Please try again.");
    }
  };

  const audienceType = audience.audience_type;
  const isManualAudience = audienceType === "manual";
  const isCategoryAudience = audienceType === "category";
  const isPlatformAudience = audienceType === "platform";
  const rosterCount = Number(audience.custom_creator_count ?? 0);
  const rosterPlatformLabel =
    (audience.platform && PLATFORMS[audience.platform]?.name) || audience.platform || "";
  const audiencePlatform = audience.category_platform || audience.platform;
  const audiencePlatformIcon = getPlatformIcon(audiencePlatform);
  const audiencePlatformLabel = getPlatformLabel(audiencePlatform);

  const refreshAudienceFromApi = () => {
    if (!hash || !onAudienceUpdated) return;
    audiencesService.getAudience(hash, (res) => onAudienceUpdated(res.data));
  };

  const nameTrimmed = nameDraft.trim();
  const savedNameTrimmed = (audience.name || "").trim();
  const nameDirty = nameTrimmed !== savedNameTrimmed;

  const handleSaveAudienceName = () => {
    if (!hash || nameSaving) return;
    setNameSaving(true);
    audiencesService.patchAudience(
      hash,
      { name: nameTrimmed || "" },
      (response) => {
        setNameSaving(false);
        if (onAudienceUpdated && response.data) {
          onAudienceUpdated(response.data);
        }
      },
      (error) => {
        setNameSaving(false);
        alert(getApiErrorMessage(error, "Could not update audience name. Please try again."));
      }
    );
  };

  const sectionCardClass = "bg-theme-secondary rounded-lg border border-theme-secondary p-6 space-y-8";

  const configurationFields = (
    <>
      <div>
        <h3 className="text-base font-semibold text-theme-text mb-1">Audience name</h3>
        <p className="text-sm text-theme-text opacity-70 mb-4">
          Optional label for this audience. It appears in the header and lists. Leave blank to fall back to the
          category name where applicable.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 min-w-0">
            <label htmlFor="audience-settings-name" className="sr-only">
              Audience name
            </label>
            <input
              id="audience-settings-name"
              type="text"
              maxLength={255}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              className="w-full rounded-md border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text placeholder:text-theme-text placeholder:opacity-50 focus:outline-none focus:ring-2 focus:ring-theme-primary/40"
              placeholder={audience.category_name ? `Default: ${audience.category_name}` : "Name this audience"}
            />
          </div>
          <button
            type="button"
            onClick={handleSaveAudienceName}
            disabled={nameSaving || !nameDirty}
            className="shrink-0 px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {nameSaving ? "Saving…" : "Save name"}
          </button>
        </div>
      </div>

      {isCategoryAudience ? (
        <div className="pt-6 border-t border-theme-secondary">
          <h3 className="text-base font-semibold text-theme-text mb-1">Category</h3>
          <p className="text-sm text-theme-text opacity-70 mb-4">
            Category is set when the audience is created. It cannot be changed here.
          </p>
          <div className="rounded-md border border-theme-secondary bg-theme-main px-4 py-3 text-sm text-theme-text">
            <span>
              {audience.category_emoji ? `${audience.category_emoji} ` : ""}
              {audience.category_name || "Category"}
            </span>
          </div>
        </div>
      ) : null}

      {isPlatformAudience ? (
        <div className="pt-6 border-t border-theme-secondary">
          <h3 className="text-base font-semibold text-theme-text mb-1">Platform</h3>
          <p className="text-sm text-theme-text opacity-70 mb-4">
            Platform is set when the audience is created. It cannot be changed here.
          </p>
          <div className="rounded-md border border-theme-secondary bg-theme-main px-4 py-3 text-sm text-theme-text flex items-center gap-2">
            {audiencePlatformIcon ? (
              <span
                className="shrink-0 flex items-center justify-center w-4 h-4"
                style={{ color: getPlatformColor(audiencePlatform) || "currentColor" }}
                aria-hidden
              >
                {React.cloneElement(audiencePlatformIcon, { className: "w-4 h-4" })}
              </span>
            ) : null}
            <span>{audiencePlatformLabel || audiencePlatform || "Platform"}</span>
          </div>
        </div>
      ) : null}

      {isManualAudience ? (
        <div className="pt-6 border-t border-theme-secondary">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
            <h3 className="text-base font-semibold text-theme-text">Hand-picked creators</h3>
            <span className="text-sm text-theme-text opacity-70">
              {rosterCount} linked{rosterPlatformLabel ? ` · ${rosterPlatformLabel}` : ""}
            </span>
          </div>
          <p className="text-sm text-theme-text opacity-70 mb-4">
            Add or remove creators by handle on this audience's platform. Processing runs automatically for newly
            added creators.
          </p>
          <button
            type="button"
            onClick={() => setRosterOpen(true)}
            className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
          >
            Manage creators
          </button>
        </div>
      ) : null}
    </>
  );

  const syncFields = (
    <>
      <div className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-theme-text mb-1">Sync Creators</h3>
          <p className="text-sm text-theme-text opacity-70">Refresh creator data and fit scores</p>
        </div>
        <AudienceUploadTimelineHistogram audienceHash={hash} />
      </div>

      <div className="pt-6 border-t border-theme-secondary">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <h4 className="text-base font-medium text-theme-text mb-1">Sync Audience</h4>
            <p className="text-sm text-theme-text opacity-70">
              Re-fetch data for existing creators and update their fit scores. Opens a progress view while the job
              runs.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSync}
            disabled={isSyncing}
            className="shrink-0 px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-center"
          >
            {isSyncing ? "Starting…" : "Sync Now"}
          </button>
        </div>
      </div>
    </>
  );

  const manageFields = (
    <div className="pt-0">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-red-500 mb-1">Danger Zone</h3>
        <p className="text-sm text-theme-text opacity-70">Irreversible and destructive actions</p>
      </div>

      <div className="pt-4 border-t border-red-500/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h4 className="text-base font-medium text-theme-text mb-1">Delete Audience</h4>
            <p className="text-sm text-theme-text opacity-70">
              Once you delete an audience, there is no going back. Please be certain.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeletingAudience}
            className="shrink-0 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-auto"
          >
            {isDeletingAudience ? "Deleting..." : "Delete Audience"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex items-start gap-6 max-w-5xl">
        <div className="w-56 flex-shrink-0 hidden sm:block">
          <h3 className="text-lg font-semibold text-theme-text mb-1">Settings</h3>
          <nav className="space-y-0.5">
            {SETTINGS_NAV.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.scrollId)}
                className={`w-full text-left px-4 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section.id
                    ? "bg-theme-active-nav text-theme-text font-medium"
                    : "text-theme-text hover:bg-theme-secondary"
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-1 min-w-0 flex-col gap-12 pb-16">
          <div className="mb-2 text-theme-text sm:hidden">
            <label className="block text-xs font-medium text-theme-text opacity-80 mb-1">Section</label>
            <select
              value={activeSection}
              onChange={(e) => {
                const id = e.target.value;
                const s = SETTINGS_NAV.find((x) => x.id === id);
                if (s) scrollToSection(s.scrollId);
              }}
              className="w-full rounded-md border border-theme-secondary bg-theme-main text-theme-text px-3 py-2 text-sm"
            >
              {SETTINGS_NAV.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.label}
                </option>
              ))}
            </select>
          </div>

          <section id="audience-settings-configuration" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-theme-text mb-1">Configuration</h2>
            <p className="text-sm text-theme-text opacity-70 mb-3">{SETTINGS_NAV[0].subtitle}</p>
            <div className={sectionCardClass}>{configurationFields}</div>
          </section>

          <section id="audience-settings-sync" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-theme-text mb-1">Sync Status</h2>
            <p className="text-sm text-theme-text opacity-70 mb-3">{SETTINGS_NAV[1].subtitle}</p>
            <div className={sectionCardClass}>{syncFields}</div>
          </section>

          <section id="audience-settings-manage" className="scroll-mt-6">
            <h2 className="text-lg font-semibold text-theme-text mb-1">Manage</h2>
            <p className="text-sm text-theme-text opacity-70 mb-3">{SETTINGS_NAV[2].subtitle}</p>
            <div className={`${sectionCardClass} border-red-500/30`}>{manageFields}</div>
          </section>
        </div>
      </div>

      {isManualAudience ? (
        <AudienceRosterModal
          audienceHash={hash}
          audience={audience}
          isOpen={rosterOpen}
          onClose={() => setRosterOpen(false)}
          onRosterChanged={refreshAudienceFromApi}
        />
      ) : null}
    </>
  );
};

const SettingsRoutes = ({ audience, onAudienceUpdated }) => {
  const { hash } = useParams();
  return (
    <Routes>
      <Route path="populating" element={<Navigate to={`/audience/${hash}/populating/`} replace />} />
      <Route path="populating/" element={<Navigate to={`/audience/${hash}/populating/`} replace />} />
      <Route path="configuration" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="configuration/" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="sync" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="sync/" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="management" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="management/" element={<LegacyAudienceSettingsRedirect />} />
      <Route path="*" element={<SettingsPage audience={audience} onAudienceUpdated={onAudienceUpdated} />} />
    </Routes>
  );
};

export default SettingsRoutes;
